import { ArrowUpTrayIcon, FolderOpenIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { useRef, useState } from "react";
import * as React from "react";
import { Button, Dropdown, DropdownContent, DropdownItem } from "components/elements";
import { ManageAttachmentsModal } from "components/elements/ManageAttachmentsModal";
import { UploadDocument } from "components/form/UploadDocument";
import type { NotifyContext, UploadContext } from "hooks/useUploadFiles";
import { useUploadFiles } from "hooks/useUploadFiles";
import { ClientDocument } from "@compass/shared/types";

interface AttachFilesButtonProps {
    /** Already-attached docs — used to pre-select rows in the previous-files picker. */
    attachments: ClientDocument[];
    /** Called with newly chosen/uploaded docs. Callers de-dupe by id. */
    onAdd: (docs: ClientDocument[]) => void;
    uploadContext: UploadContext;
    notifyContext: NotifyContext;
    /** Which buckets the previous-attachments picker may list. */
    showPrivateFiles?: boolean;
    showPublicFiles?: boolean;
    /** Default visibility of the detailed upload modal. */
    defaultPublic?: boolean;
    /** Title of the detailed upload modal. */
    uploadTitle?: string;
    blur?: boolean;
    /** "icon" renders a single toolbar icon instead of a labelled button. */
    appearance?: "button" | "icon";
    size?: "normal" | "smaller" | "smallest";
    className?: string;
}

/**
 * PRTL-1125: the single attachment entry point for every composer.
 *
 * Composers used to line up three buttons side by side — "Attach files"
 * (multi-select), "New attachment" (single file with title/description) and
 * "Previous attachments" (re-attach an already-uploaded file). Product asked for
 * one button, so the three paths became one menu: nothing was removed, only the
 * clutter. The multi-select upload is listed first because it is the common case.
 */
export const AttachFilesButton = ({
    attachments,
    onAdd,
    uploadContext,
    notifyContext,
    showPrivateFiles = false,
    showPublicFiles = true,
    defaultPublic,
    uploadTitle = "Upload New Document",
    blur = false,
    appearance = "button",
    size = "normal",
    className,
}: AttachFilesButtonProps) => {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showManageModal, setShowManageModal] = useState(false);
    const multiFileInputRef = useRef<HTMLInputElement | null>(null);

    const { uploadFiles, isUploading } = useUploadFiles(uploadContext, notifyContext);

    // Reset the input value so re-picking the same file still fires onChange.
    const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        e.target.value = "";
        if (files.length === 0) return;
        const docs = await uploadFiles(files);
        if (docs.length > 0) onAdd(docs);
    };

    // Radix closes the menu before onSelect's frame ends; opening the picker or a
    // modal on the next tick avoids the click being swallowed by that teardown.
    const defer = (fn: () => void) => () => window.setTimeout(fn, 0);

    const trigger =
        appearance === "icon" ? (
            <button
                type="button"
                title="Attach files"
                aria-label="Attach files"
                disabled={isUploading}
                className="rounded p-1 text-gray-600 hover:bg-gray-300 disabled:opacity-50"
            >
                <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
        ) : (
            <Button
                type="button"
                variant="secondary"
                size={size}
                className={className ?? "text-sm"}
                isLoading={isUploading}
                disabled={isUploading}
            >
                Attach files
            </Button>
        );

    return (
        <>
            <Dropdown
                content={
                    <DropdownContent
                        align="start"
                        arrow={false}
                        bordered
                        portal
                        className="z-[60] w-[240px] shadow-lg"
                    >
                        <DropdownItem
                            size="smaller"
                            onSelect={defer(() => multiFileInputRef.current?.click())}
                        >
                            <ArrowUpTrayIcon className="mr-2 h-4 w-4 shrink-0" />
                            Upload from device
                        </DropdownItem>
                        <DropdownItem
                            size="smaller"
                            onSelect={defer(() => setShowUploadModal(true))}
                        >
                            <PaperClipIcon className="mr-2 h-4 w-4 shrink-0" />
                            Upload with details
                        </DropdownItem>
                        <DropdownItem
                            size="smaller"
                            onSelect={defer(() => setShowManageModal(true))}
                        >
                            <FolderOpenIcon className="mr-2 h-4 w-4 shrink-0" />
                            Previous attachments
                        </DropdownItem>
                    </DropdownContent>
                }
            >
                {trigger}
            </Dropdown>

            <input
                ref={multiFileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFilesSelected}
            />

            <UploadDocument
                isOpen={showUploadModal}
                setIsOpen={setShowUploadModal}
                onUpload={(doc) => onAdd([doc])}
                button={null}
                title={uploadTitle}
                blur={blur}
                defaultPublic={defaultPublic}
                notifyContext={notifyContext}
            />

            <ManageAttachmentsModal
                isOpen={showManageModal}
                setIsOpen={setShowManageModal}
                onAttach={(docs) => {
                    onAdd(docs);
                    setShowManageModal(false);
                }}
                alreadyAttachedIds={new Set(attachments.map((a) => a.id))}
                button={null}
                blur={blur}
                showPrivateFiles={showPrivateFiles}
                showPublicFiles={showPublicFiles}
            />
        </>
    );
};
